import React, { useEffect, useState } from "react";
import styles from './EvaluationSummary.module.scss';
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../../../components/icon/Icon";
import EvaluationSummaryCard from "../../../components/evaluationSummaryCard/EvaluationSummaryCard";
import axiosClient from "../../../api/axiosClient";
import { useToasts } from "../../../contexts/ToastContext";
import Modal from "../../../components/modal/Modal";
import Button from "../../../components/button/Button";

const EvaluationSummary = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [student, setStudent] = useState(null);
  const [openCard, setOpenCard] = useState(0);
  const [isNoteVisible, setIsNoteVisible] = useState(false);
  const [score, setScore] = useState(0);
  const [note, setNote] = useState('');
  const [questions, setQuestions] = useState([]);
  const [isCompleted,  setIsCompleted] = useState(true);
  const { evaluationId } = useParams();
  const { showToast } = useToasts();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await axiosClient.get(`evaluations/summary/${evaluationId}`)
      .then((response) => {
        if(response.data) {
          console.log(response.data);
          setStudent({...response.data?.student});
          setScore(response.data?.overallScore);
          setNote(response.data?.note);
          setQuestions(response.data?.questions);
          setIsCompleted(response.data?.status === 'Completed');
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
    };

    fetchData();
  }, [evaluationId, showToast]);

  const handleSubmit = async () => {
    setIsLoading(true);
    await axiosClient.patch(`evaluations/${evaluationId}/submit`)
    .then((data) => {
      showToast(data.message);
      setIsCompleted(true);
      navigate(student ? `/student-overview/${student.id ?? ''}` : `/evaluations`)
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <div className={styles.container}>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Home', path: '/' },
            ...(student ? [
              { 
                label: `${student.firstName ?? ''} ${student.lastName ?? ''}`, 
                path: `/student-overview/${student.id ?? ''}` 
              }
            ] : []),
            { label: `Evaluation ID - ${evaluationId}` }
          ]}
        />
        {!isCompleted && <div className={styles.cta}>
          <Button
            label={
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="8"
                  viewBox="0 0 11 8"
                  fill="none"
                >
                  <path
                    d="M0.646447 3.64645C0.451184 3.84171 0.451184 4.15829 0.646447 4.35355L3.82843 7.53553C4.02369 7.7308 4.34027 7.7308 4.53553 7.53553C4.7308 7.34027 4.7308 7.02369 4.53553 6.82843L1.70711 4L4.53553 1.17157C4.7308 0.976311 4.7308 0.659728 4.53553 0.464466C4.34027 0.269204 4.02369 0.269204 3.82843 0.464466L0.646447 3.64645ZM1 4.5H11V3.5H1V4.5Z"
                    fill="black"
                  />
                </svg>
                <span> Back to Evaluation</span>
              </>
            }
            size="small"
            variant="grey"
            onClick={() => navigate(`/student-overview/evaluation/${evaluationId}`)}
          />
          <Button
            label="Submit Evaluation"
            onClick={handleSubmit}
            size="small"
          />
        </div>}
      </div>
      <div className={styles.scrollContainer}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.left}>
              <h1>Summary</h1>
              <button 
                onClick={() => setIsNoteVisible(!isNoteVisible)}
                className={styles.noteButton}
              >
                <Icon name="clipboard" />Confidential Notes
              </button>
              {isNoteVisible && <Modal>
                <div className={styles.confidentialNotes}>
                  <h5>Confidential Notes</h5>
                  <p>{ note ?? 'Notes not available' }</p>
                  <div className={styles.footer}>
                    <div className={styles.help}>
                      <Icon name="lock" />
                      <div className={styles.text}>
                        <label>Private </label>
                        <span>Only visible to the director / program admin</span>
                      </div>
                    </div>
                    <Button
                      label="Close"
                      variant="black"
                      size="small"
                      onClick={() => setIsNoteVisible(false)}
                    />
                  </div>
                </div>       
              </Modal>}
            </div>
            <div className={styles.right}>
              <div className={styles.scoreBox}>
                Overall Score <strong>{score}</strong>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.section}>
          <div className={styles.sectionContent}>
            {questions.map((question) => 
              <EvaluationSummaryCard
                key={`question-${question.id}`}
                isOpened={openCard === question.id}
                onClick={() => setOpenCard(openCard !== question.id ? question.id : 0)}
                data={question}
              />)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluationSummary;